import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PatientTable from "./PatientTable";
import { useNavigate } from "react-router-dom";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";

const AllPatient = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [resetFlag, setResetFlag] = useState(false);

  const itemsPerPage = 10;
  const navigate = useNavigate();
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        const response = await axios.get("https://api.muktihospital.com/api/patient", {
          headers: { "x-api-key": API_KEY },
        });
        const patientData = Array.isArray(response.data) ? response.data : [];
        // console.log("✅ API Response:", response.data);
        setPatients(patientData);
        setFilteredPatients(patientData);
      } catch (error) {
        console.error("❌ Error fetching patients:", error);
        toast.error("Failed to load patients.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const resetSelection = () => {
    setResetFlag((prev) => !prev);
  };

  // Handle Search
  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    filterPatients(term);
  };

  // Filter patients based on the search term
  const filterPatients = (term) => {
    const filtered = patients.filter((patient) => {
      const id = patient.id?.toLowerCase() || "";
      const name = patient.name?.toLowerCase() || "";
      const phoneNumber = patient.phoneNumber?.toLowerCase() || "";
      return id.includes(term) || name.includes(term) || phoneNumber.includes(term);
    });
    setFilteredPatients(filtered);
  };

  // Open confirm modal for delete action
  const openDeleteModal = (patientIds) => {
    setPatientToDelete(patientIds);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setPatientToDelete(null);
  };

  // Handle patient deletion
  const handleConfirmDelete = async (ids) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    if (idArray.length === 0) return;

    try {
      await Promise.all(
        idArray.map((id) =>
          axios.delete(`https://api.muktihospital.com/api/patient/delete/${id}`, {
            headers: { "x-api-key": API_KEY },
          })
        )
      );
      const updatedPatients = patients.filter((patient) => !idArray.includes(patient.id));
      setPatients(updatedPatients);
      setFilteredPatients(updatedPatients);
      toast.success(`Patient${idArray.length > 1 ? "s" : ""} deleted successfully`);
      resetSelection();
    } catch (error) {
      console.error("❌ Error deleting patient(s):", error);
      toast.error("Failed to delete patient(s)");
    }
  };

  // Edit Patient
  const handleEdit = (id) => {
    navigate(`/edit-patient/${id}`);
  };

  // Breadcrumbs for navigation
  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Patients"},
    { label: "All Patients" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading title="All Patients" breadcrumbs={breadcrumbs} />
      <PatientTable
        data={filteredPatients}
        setData={setFilteredPatients}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        openDeleteModal={openDeleteModal}
        handleEdit={handleEdit}
        resetFlag={resetFlag}
        itemsPerPage={itemsPerPage}
        isLoading={loading}
      />
       <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        ids={patientToDelete}
        entityName="patient"
        title="Confirm Delete"
        message={
          Array.isArray(patientToDelete) && patientToDelete.length > 1
            ? `Are you sure you want to delete these ${patientToDelete.length} doctors?`
            : "Are you sure you want to delete this patient?"
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AllPatient;